import { makeStyles } from '@material-ui/styles'
import {
  bottomLeft,
  bottomMiddle,
  middleRight,
  topMiddle,
  topRight,
} from 'pf9-ui-components/built/elements/menu/defaults'
import Text, { TextProps, TextVariant } from 'pf9-ui-components/built/elements/Text'
import Tooltip from 'core/elements/tooltip'
import React, { useEffect, useRef, useState } from 'react'

interface EllipsisTextProps extends TextProps {
  variant?: TextVariant
  children: any
  component?: React.ElementType
  title?: string
  maxWidth?: number
  showTooltip?: boolean
  className?: string
  alignTooltip?: 'bottomLeft' | 'bottomMiddle' | 'middleRight' | 'topMiddle' | 'topRight'
  id?: string
}

const useStyles = makeStyles((theme: any) => ({
  textWithEllipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    color: theme.components.card.text,
  },
  customTooltip: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tooltipText: {
    color: theme.palette.common.white,
  },
}))

const EllipsisText = ({
  children,
  variant = 'body1',
  component,
  title,
  maxWidth,
  showTooltip,
  className,
  alignTooltip: align,
  id,
}: EllipsisTextProps) => {
  const classes = useStyles()
  const elementRef = useRef(null)
  const [isTextTruncated, setIsTextTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (elementRef?.current) {
        const element = elementRef.current as HTMLElement
        // Check if text is truncated by comparing scrollWidth with the effective width
        // When maxWidth is set, we compare against the minimum of offsetWidth and maxWidth
        // to accurately detect truncation even when content doesn't fill the maxWidth
        const effectiveWidth = Math.min(element.offsetWidth, maxWidth || Infinity)
        const isTruncated = element.scrollWidth > effectiveWidth

        setIsTextTruncated(isTruncated)
      }
    }

    // Use requestAnimationFrame to ensure DOM has been updated
    const rafId = requestAnimationFrame(() => {
      checkTruncation()
    })

    return () => cancelAnimationFrame(rafId)
  }, [children, title, maxWidth])

  const alignment = {
    bottomLeft,
    bottomMiddle,
    middleRight,
    topMiddle,
    topRight,
  }

  return (
    <>
      {showTooltip && isTextTruncated ? (
        <Tooltip
          className={classes.customTooltip}
          message={
            showTooltip && (
              <Text variant="body2" lineClamp={10} className={classes.tooltipText}>
                {title}
              </Text>
            )
          }
          align={alignment[align]?.align}
        >
          <Text
            id={id}
            variant={variant}
            component={component}
            ref={elementRef}
            className={`${classes.textWithEllipsis} ${className}`}
            style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
          >
            {children}
          </Text>
        </Tooltip>
      ) : (
        <Text
          id={id}
          variant={variant}
          component={component}
          ref={elementRef}
          title={isTextTruncated ? title : undefined}
          className={`${classes.textWithEllipsis} ${className}`}
          style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
        >
          {children}
        </Text>
      )}
    </>
  )
}

export default EllipsisText
